import { CUSTOM_EXPIRY_TIME, EXPIRY_LABELS } from "@/constants/campaignBuilder";
import {
  DELIVERY_METHOD_LABELS,
  EXPIRATION_DEFAULT_LABEL,
  SCHEDULE_SEND_NOW_LABEL,
  SENDING_TO_US_ONLY_LABEL,
  SENDING_TO_WORLDWIDE_LABEL,
} from "@/constants/review";
import type {
  ActivationMode,
  ExpiryMode,
  NotificationMode,
  ScheduleMode,
} from "@/context/CampaignBuilderContext";
import { countryNameFromCode, resolveCountryCodeOrUs } from "@/lib/country";
import { isAnonymousLinks, isSelfSendMode } from "@/utils/campaignMode-helper";
import { zonedWallTimeToIso } from "@/utils/timezone-helper";

/**
 * Pure string helpers for the Settings Review card. The prototype derives these
 * values by scraping the DOM (`_readScheduleSummary` ~lines 23427-23447 and
 * `_readExpirationSummary` ~lines 23450-23471 of
 * Campaign-Builder-Master-Prototype-Stage2-Revisions.html); this port keeps the
 * OUTPUT STRINGS identical while reading from the real context state instead.
 */

const SUMMARY_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
};

// The prototype's custom-expiration branch always closes at a fixed time — the app has no
// expiration time picker, mirroring `dateEl.value + 'T19:00:00'` formatted back out as '07:00 PM'
// (~line 23464). Now read in the campaign's zone and labelled ("07:00 PM EDT"), since that is the
// instant the API stores (CUSTOM_EXPIRY_TIME).
const EXPIRATION_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  timeZoneName: "short",
};

const SCHEDULE_DIFFERENT_PER_RECIPIENT_LABEL = "Different per recipient";

// A dated send with no date chosen yet is still a SCHEDULED send, so it must not
// read as an immediate one. The prototype's `_readScheduleSummary()` falls back
// to this string at the end of its "later/certain" branch (~line 23445), only
// returning 'Send Now' for the ASAP radio or for no selection at all.
const SCHEDULE_LATER_LABEL = "Scheduled for later";

export function getScheduleSummary(input: {
  scheduleMode: ScheduleMode;
  scheduleDate: string;
  scheduleTime: string;
  scheduleTz: string;
}): string {
  const { scheduleMode, scheduleDate, scheduleTime } = input;

  if (scheduleMode === "asap") return SCHEDULE_SEND_NOW_LABEL;
  if (scheduleMode === "different") return SCHEDULE_DIFFERENT_PER_RECIPIENT_LABEL;
  if (!scheduleDate) return SCHEDULE_LATER_LABEL;

  const date = new Date(`${scheduleDate}T${scheduleTime || "09:00"}`);
  return date.toLocaleDateString(undefined, SUMMARY_DATE_FORMAT);
}

export function getExpirationSummary(input: {
  expiryMode: ExpiryMode;
  customExpiryDate: string;
  // Manual campaigns anchor expiry on the link ACTIVATION date, not a send date — swap the phrase
  // to match the Expiration accordion. (mode-agnostic expiry;
  // only the anchor wording differs).
  notificationMode?: NotificationMode;
  /** The campaign's Schedule zone — the close is 7:00 PM THERE. */
  timeZone: string;
}): string {
  const { expiryMode, customExpiryDate, notificationMode, timeZone } = input;

  if (expiryMode === "custom") {
    // A custom expiry with no date picked reads as "Custom date", NOT as the
    // 60-day default it isn't — the prototype's `_readExpirationSummary()`
    // returns 'Custom date' here (~line 23470), which is what EXPIRY_LABELS
    // already holds for this mode.
    if (!customExpiryDate) return EXPIRY_LABELS.custom;
    const iso = zonedWallTimeToIso(customExpiryDate, CUSTOM_EXPIRY_TIME, timeZone);
    if (!iso) return EXPIRY_LABELS.custom;
    const date = new Date(iso);
    const day = date.toLocaleDateString(undefined, { ...SUMMARY_DATE_FORMAT, timeZone });
    const time = date.toLocaleTimeString("en-US", { ...EXPIRATION_TIME_FORMAT, timeZone });
    return `${day} at ${time}`;
  }

  const label = EXPIRY_LABELS[expiryMode] ?? EXPIRATION_DEFAULT_LABEL;
  // Self-send (manual + universal) anchors expiry on the link ACTIVATION date, not a send date.
  return notificationMode !== undefined && isSelfSendMode(notificationMode)
    ? label.replace("send date", "activation date")
    : label;
}

// The prototype's `_readActivationSummary()` — "Immediately at launch" for immediate activation, or
// the formatted go-live moment. Self-send (manual + universal), the "Links Activate" review row. The
// ticket asks for "[date] at [time] [TZ]", so a scheduled activation renders the full instant in the
// campaign's zone (same pattern as the custom-expiry row), not the date alone.
export function getActivationSummary(input: {
  activationMode: ActivationMode;
  activationDate: string;
  activationTime: string;
  timeZone: string;
}): string {
  const { activationMode, activationDate, activationTime, timeZone } = input;
  if (activationMode !== "scheduled") return "Immediately at launch";
  if (!activationDate) return "Scheduled";
  const iso = zonedWallTimeToIso(activationDate, activationTime || "09:00", timeZone);
  if (!iso) return "Scheduled";
  const date = new Date(iso);
  const day = date.toLocaleDateString(undefined, { ...SUMMARY_DATE_FORMAT, timeZone });
  const time = date.toLocaleTimeString("en-US", { ...EXPIRATION_TIME_FORMAT, timeZone });
  return `${day} at ${time}`;
}

/**
 * "Sending to" is computed from the STEP 2 recipient list, not Step 1 — Step 1 selects the base
 * collection, not a destination. Each recipient's country resolves to an ISO code, with blank /
 * unmapped / unresolved treated as US (an empty cell defaults to US). Then:
 *   - no recipients, or all US -> "US Only"
 *   - exactly one non-US country -> "<Country> Only" (e.g. "Australia Only")
 *   - two or more distinct countries -> "Worldwide" (a blank + any non-US is two: US + that country)
 */
export function getSendingToLabel(recipients: { recipientCountry?: string }[]): string {
  const codes = new Set(recipients.map((r) => resolveCountryCodeOrUs(r.recipientCountry)));
  if (codes.size >= 2) return SENDING_TO_WORLDWIDE_LABEL;
  const only = [...codes][0] ?? "US"; // size 0 (no recipients) and all-US both read as US
  return only === "US" ? SENDING_TO_US_ONLY_LABEL : `${countryNameFromCode(only) ?? only} Only`;
}

/**
 * "Sending to" for the whole campaign: quantity-only manual links have no recipient list, so the
 * stored rows must not drive the label (GFG-442) — it reads as the empty-list default.
 */
export function getCampaignSendingToLabel(input: {
  recipients: { recipientCountry?: string }[];
  notificationMode: NotificationMode;
  linkToList: boolean | null;
}): string {
  return getSendingToLabel(
    isAnonymousLinks(input.notificationMode, input.linkToList) ? [] : input.recipients,
  );
}

export function getDeliveryMethodLabel(mode: NotificationMode): string {
  return DELIVERY_METHOD_LABELS[mode];
}
